import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
} from '@angular/core';
import {
  RadioCardComponent,
  RadioCardGroupComponent,
  TagComponent,
  TagVariant,
} from '@catch/general-ui';
import { ThemingService } from '@catch/services';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { StripeBillingInterval } from '../../../enums/stripe.enum';
import { StripeProduct } from '../../../interfaces/stripe';
import { FontawesomeService } from '../../../services/fontawesome.service';

enum BasePlanKey {
  FREE = 'free',
  STARTER = 'starter',
  GROWTH = 'growth',
  SCALE = 'scale',
}

@Component({
  selector: 'app-base-plan-selector',
  imports: [
    CurrencyPipe,
    FontAwesomeModule,
    NgxSkeletonLoaderModule,
    RadioCardComponent,
    RadioCardGroupComponent,
    TagComponent,
    TranslocoPipe,
  ],
  templateUrl: './base-plan-selector.component.html',
  styleUrl: './base-plan-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BasePlanSelectorComponent {
  readonly products = input.required<StripeProduct[]>();
  readonly loaded = input.required<boolean>();
  readonly billingInterval = input.required<StripeBillingInterval>();
  readonly disabled = input(false);
  readonly currentPriceId = input<string | null>(null);
  readonly selectedPriceId = model<string | null>(null);

  readonly TagVariant = TagVariant;
  readonly featureIndexes = [1, 2, 3, 4, 5, 6] as const;
  readonly themingService = inject(ThemingService);
  readonly fontawesomeService = inject(FontawesomeService);

  readonly hasProducts = computed(() => this.products().length > 0);

  /**
   * Paid Base plans shown as equal-width cards.
   * @returns {StripeProduct[]}
   */
  readonly paidProducts = computed(() =>
    this.products().filter((product) => !this.isNoSubscription(product)),
  );

  /**
   * Opt-out catalog price rendered as a full-width card below the paid plans.
   * @returns {StripeProduct | null}
   */
  readonly noSubscriptionProduct = computed(
    () =>
      this.products().find((product) => this.isNoSubscription(product)) ?? null,
  );

  /**
   * Resolves the display title for a catalog price.
   * @param {StripeProduct} product Catalog price
   * @returns {string}
   */
  productTitle(product: StripeProduct): string {
    return product.product?.name ?? product.id;
  }

  /**
   * Whether the catalog price is the opt-out (no subscription) option.
   * @param {StripeProduct} product Catalog price
   * @returns {boolean}
   */
  isNoSubscription(product: StripeProduct): boolean {
    return this.planKey(product) === BasePlanKey.FREE;
  }

  /**
   * Returns the monthly display amount in major currency units.
   * @param {StripeProduct} product Catalog price
   * @returns {number}
   */
  monthlyDisplayAmount(product: StripeProduct): number {
    const amount = product.unit_amount / 100;
    return this.billingInterval() === StripeBillingInterval.YEAR
      ? amount / 12
      : amount;
  }

  /**
   * Maps a catalog price to a known Base plan.
   * @param {StripeProduct} product Catalog price
   * @returns {BasePlanKey | null}
   */
  planKey(product: StripeProduct): BasePlanKey | null {
    const normalizedName = (product.product?.name ?? '').toLowerCase();
    if (
      normalizedName.includes(BasePlanKey.FREE) ||
      product.unit_amount === 0
    ) {
      return BasePlanKey.FREE;
    }
    if (normalizedName.includes(BasePlanKey.STARTER)) {
      return BasePlanKey.STARTER;
    }
    if (normalizedName.includes(BasePlanKey.GROWTH)) {
      return BasePlanKey.GROWTH;
    }
    if (normalizedName.includes(BasePlanKey.SCALE)) {
      return BasePlanKey.SCALE;
    }
    const nestedProduct = product.product as
      | (StripeProduct['product'] & { metadata?: Record<string, string> })
      | undefined;
    const level = +(
      product.metadata?.sub_level ??
      nestedProduct?.metadata?.sub_level ??
      0
    );
    if (level === 1) {
      return BasePlanKey.STARTER;
    }
    if (level === 2) {
      return BasePlanKey.GROWTH;
    }
    if (level >= 3) {
      return BasePlanKey.SCALE;
    }
    return null;
  }

  /**
   * Checks whether a product is the company's current Base plan,
   * including the no-subscription option when that is the live state.
   * @param {StripeProduct} product Catalog price
   * @returns {boolean}
   */
  isCurrent(product: StripeProduct): boolean {
    return product.id === this.currentPriceId();
  }

  /**
   * Checks whether Growth should carry the recommendation badge.
   * @param {StripeProduct} product Catalog price
   * @returns {boolean}
   */
  isRecommended(product: StripeProduct): boolean {
    return (
      !this.isCurrent(product) && this.planKey(product) === BasePlanKey.GROWTH
    );
  }
}
